import React, { useState } from 'react';
import RegisterScreen from './RegisterScreen';
import ConfirmScreen from './ConfirmScreen';
import LoginScreen from './LoginScreen';
import MfaScreen from './MfaScreen';
import ForgotPasswordScreen from './ForgotPasswordScreen';

type View = 'login' | 'register' | 'confirm' | 'mfa' | 'forgot';

/**
 * State-based navigation across the unauthenticated screens. Kept dependency
 * free (no router) to stay lean for the prototype.
 */
export default function AuthFlow() {
    const [view, setView] = useState<View>('login');
    const [pendingEmail, setPendingEmail] = useState('');
    // Held only during the email-MFA step so "Resend code" can re-initiate
    // sign-in without retyping. Cleared when leaving the MFA screen.
    const [pendingPassword, setPendingPassword] = useState('');
    // Optional message shown on the confirm screen (e.g. when a user tries to
    // sign in before verifying their email).
    const [confirmNotice, setConfirmNotice] = useState<string | undefined>(undefined);

    if (view === 'register') {
        return (
            <RegisterScreen
                onRegistered={(email) => {
                    setPendingEmail(email);
                    setConfirmNotice(undefined);
                    setView('confirm');
                }}
                onGoToLogin={() => setView('login')}
            />
        );
    }

    if (view === 'confirm') {
        return (
            <ConfirmScreen
                email={pendingEmail}
                notice={confirmNotice}
                onConfirmed={() => setView('login')}
            />
        );
    }

    if (view === 'mfa') {
        return (
            <MfaScreen
                email={pendingEmail}
                password={pendingPassword}
                onBack={() => {
                    setPendingPassword('');
                    setView('login');
                }}
            />
        );
    }

    if (view === 'forgot') {
        return <ForgotPasswordScreen onBack={() => setView('login')} />;
    }

    return (
        <LoginScreen
            onGoToRegister={() => setView('register')}
            onForgotPassword={() => setView('forgot')}
            onMfaRequired={(email, password) => {
                setPendingEmail(email);
                setPendingPassword(password);
                setView('mfa');
            }}
            onConfirmationRequired={(email) => {
                setPendingEmail(email);
                setConfirmNotice(
                    'Your email isn\u2019t verified yet. Enter the code we sent you, or resend a new one.',
                );
                setView('confirm');
            }}
        />
    );
}
